'use client'

import { useEffect, useRef } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { Button } from '@/app/components/atoms/Button'
import { ProgressCircleIndeterminate } from '@/app/components/atoms/ProgressCircleIndeterminate'
import { VirtualizedList } from '@/app/components/atoms/VirtualizedList'
import { cx } from '@/app/lib/utils'
import { useInfiniteList } from '@/app/lib/hooks/useInfiniteList'

import { DEFAULT_ERROR_MESSAGE, DEFAULT_LOADING_LABEL, DEFAULT_RETRY_LABEL } from './constants'
import styles from './InfiniteVirtualizedList.module.scss'
import type { InfiniteVirtualizedListProps } from './types'

/**
 * `InfiniteVirtualizedList` — conecta `useInfiniteList` + `VirtualizedList`
 * (scroll infinito + windowing, ambos genéricos) con lo que casi todo listado
 * paginado de este tipo necesita: reflejar `?page=` en la URL tras el scroll,
 * los estados de carga/error/vacío, y un hueco explícito (`paginationLinks`)
 * para la paginación HTML rastreable — como prop, no como hermano suelto que
 * cada página tendría que acordarse de añadir por su cuenta.
 *
 * `paginationLinks` (típicamente `SeoPaginationLinks`, un Server Component)
 * llega ya resuelto desde la página consumidora (Server): este componente es
 * Client y no puede importar un Server Component directamente, así que el
 * único punto de integración posible es recibirlo pre-renderizado como prop
 * (patrón "Server Component como children/prop de un Client Component").
 */
export function InfiniteVirtualizedList<TItem>({
  initialItems,
  initialPage,
  totalPages,
  pageSize,
  threshold,
  getItemKey,
  fetchPage,
  onPageLoaded,
  renderItem,
  estimatedRowHeight,
  columns,
  columnGap,
  rowGap,
  overscanRows,
  insertAfterRow,
  insertContent,
  insertEstimatedHeight,
  pageParam,
  paginationLinks,
  emptyState,
  footer,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  retryLabel = DEFAULT_RETRY_LABEL,
  loadingLabel = DEFAULT_LOADING_LABEL,
  className,
}: InfiniteVirtualizedListProps<TItem>) {
  const pathname = usePathname()
  const router = useRouter()
  // `.toString()` en vez del objeto `searchParams`: Next re-sincroniza ese
  // objeto (nueva referencia) en cada navegación — si el efecto dependiera
  // del objeto en sí, cada actualización de la URL dispararía el propio
  // efecto otra vez de forma indefinida.
  const searchParamsString = useSearchParams().toString()

  const { items, status, hasMore, sentinelRef, retry, currentPage } = useInfiniteList<TItem>({
    initialItems,
    initialPage,
    totalPages,
    pageSize,
    threshold,
    getItemKey,
    fetchPage,
    onPageLoaded,
  })

  // Refleja en la URL la página alcanzada por el scroll infinito (deep-link
  // compartible), sin saltar el scroll: usa `router.replace` con
  // `{ scroll: false }` — la única forma soportada de actualizar la URL sin
  // que Next.js marque el segmento recién obtenido para "scroll-into-view".
  //
  // Antes se usaba `window.history.replaceState` nativo asumiendo que
  // quedaba "fuera" del router de Next.js. No es así: cualquier
  // `pushState`/`replaceState` externo se intercepta igual (Next.js lo
  // parchea globalmente) y dispara una acción de "restauración" de
  // navegación — como esta página lee `searchParams` (es dinámica), esa
  // restauración relanza el fetch del segmento en el servidor igual que una
  // navegación real, PERO sin ninguna opción de scroll: cualquier segmento
  // con datos recién obtenidos queda marcado para scroll-into-view sin poder
  // desactivarlo (ver `node_modules/next/dist/client/components/segment-cache/
  // navigation.js`, rama `ScrollBehavior.NoScroll`, que solo aplica a
  // `ACTION_NAVIGATE`, no a `ACTION_RESTORE`). Ese marcador causaba el salto
  // de scroll al cargar cada página nueva, incluso ya con los ítems
  // acumulados correctamente (ver `useInfiniteList.ts`). `router.replace`
  // pasa por `ACTION_NAVIGATE`, que sí acepta `scroll: false` y neutraliza
  // ese marcador explícitamente.
  //
  // Solo escribe cuando cambia `currentPage` (o en el montaje, para corregir
  // un `?page=` fuera de rango), NUNCA cuando lo que cambia es la URL: una
  // navegación externa que no remonta el listado (p. ej. quitar un filtro
  // que no estaba en la URL — "Ordenar por" con su valor por defecto — o un
  // enlace de `SeoPaginationLinks`) llega aquí con la URL nueva pero con el
  // `currentPage` todavía viejo (el reset de `useInfiniteList` aún no se ha
  // aplicado). Escribir en ese render reponía el `?page=` viejo, el reset lo
  // borraba después y ambos se relanzaban el uno al otro en bucle.
  const lastSyncedPageRef = useRef<number | null>(null)
  useEffect(() => {
    if (lastSyncedPageRef.current === currentPage) return
    lastSyncedPageRef.current = currentPage
    const next = new URLSearchParams(searchParamsString)
    if (currentPage > 1) next.set(pageParam, String(currentPage))
    else next.delete(pageParam)
    const qs = next.toString()
    const url = qs ? `${pathname}?${qs}` : pathname
    if (`${window.location.pathname}${window.location.search}` !== url) {
      router.replace(url, { scroll: false })
    }
  }, [currentPage, pathname, searchParamsString, pageParam, router])

  if (items.length === 0 && status !== 'loading') {
    return (
      <>
        {emptyState}
        {paginationLinks}
      </>
    )
  }

  return (
    <div className={cx(styles.root, className)}>
      {/* Fuera de `VirtualizedList`: enlaces reales para crawlers/lectores de
          pantalla, con independencia de cuántas filas haya montado la
          virtualización en el cliente. */}
      {paginationLinks}

      <VirtualizedList
        items={items}
        itemKey={getItemKey}
        renderItem={renderItem}
        estimatedRowHeight={estimatedRowHeight}
        columns={columns}
        columnGap={columnGap}
        rowGap={rowGap}
        overscanRows={overscanRows}
        insertAfterRow={insertAfterRow}
        insertContent={insertContent}
        insertEstimatedHeight={insertEstimatedHeight}
      />

      {/* Sentinela del scroll infinito: no interactivo ni visible para lectores de pantalla. */}
      <div ref={sentinelRef} aria-hidden="true" className={styles.sentinel} />

      {status === 'error' ? (
        <div className={styles.statusRow} role="alert">
          <p className={styles.errorMessage}>{errorMessage}</p>
          <Button type="button" variant="outlined" onClick={retry}>
            {retryLabel}
          </Button>
        </div>
      ) : hasMore ? (
        // Se muestra mientras queden páginas por cargar, no solo durante la
        // petición en curso: en un scroll muy rápido se puede llegar al
        // footer antes de que el observer dispare la siguiente petición —
        // este hueco reservado evita que el listado parezca "acabado"
        // mientras de verdad quedan elementos.
        <div className={styles.statusRow}>
          <ProgressCircleIndeterminate size="s" label={loadingLabel} />
          <p className={styles.loadingCaption} aria-hidden="true">
            {loadingLabel}…
          </p>
        </div>
      ) : (
        footer
      )}
    </div>
  )
}
